import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import PageHero from "@/components/PageHero";
import LeadCTA from "@/components/LeadCTA";
import EN from "@/data/content-en";
import ZH from "@/data/content-zh";
import { PAGE_IMAGES } from "@/data/pages";

export default function ContactPage() {
  const { lang, t } = useLang();
  const c = lang === "zh" ? ZH.contact : EN.contact;

  return (
    <div className={lang === "zh" ? "lang-zh" : ""}>
      <SEO pageId="contact" title={c.title} description={c.metaDescription} image={PAGE_IMAGES.contact} />

      <PageHero pageId="contact" mainTitle={c.h1} subtitle={c.intro} image={PAGE_IMAGES.contact} />

      <section className="bg-[var(--abs-cream)] py-12" data-testid="contact-hero">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10" />
      </section>

      <section id="offices" className="bg-white py-20" data-testid="contact-offices">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
          {c.offices.map((office, i) => (
            <ScrollReveal key={office.name} delay={i * 80} className="border border-[var(--abs-line)] bg-white">
              <div className="aspect-[16/10] w-full bg-[var(--abs-soft)] overflow-hidden img-frame">
                <iframe
                  title={`Map ${office.name}`}
                  data-testid={`map-${i}`}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(office.mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
              <div className="p-8 md:p-10">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-3">{office.name}</div>
                <h3 className="serif text-2xl md:text-3xl mb-6">{office.company}</h3>

                <div className="space-y-4 text-[15px]">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 mt-1 text-[var(--abs-orange)] shrink-0" />
                    <div>
                      <div>{office.address}</div>
                      {office.extra && (
                        <div className="mt-2 text-[var(--abs-mute)]" data-testid={`office-extra-${i}`}>{office.extra}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 mt-1 text-[var(--abs-orange)] shrink-0" />
                    <div>
                      <div>{t.misc.phone}: <a className="hover:text-[var(--abs-orange)]" href={`tel:${office.landline.replace(/\s/g, "")}`}>{office.landline}</a></div>
                      <div>{t.misc.mobile}: <a className="hover:text-[var(--abs-orange)]" href={`tel:${office.mobile.replace(/\s/g, "")}`}>{office.mobile}</a></div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 mt-1 text-[var(--abs-orange)] shrink-0" />
                    <a className="hover:text-[var(--abs-orange)]" href={`mailto:${office.email}`}>{office.email}</a>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="bg-[var(--abs-beige)] py-20" data-testid="contact-hours">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-start">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-[var(--abs-orange)]">
              <Clock className="h-5 w-5" />
              <div className="text-[11px] uppercase tracking-[0.22em]">
                {lang === "zh" ? "营业时间（Canterbury 办公室）" : "Office hours (Canterbury Office)"}
              </div>
            </div>
            <h3 className="serif text-3xl md:text-5xl mt-5 leading-[1.06]">
              {lang === "zh" ? "我们的营业时间" : "When we're open"}
            </h3>
            <p className="mt-5 text-[var(--abs-mute)] text-[15px] max-w-md">
              {lang === "zh" ? "节假日休息。下班后请预约。布里斯班办公室仅限预约。" : "After-hours by appointment only. Closed on public holidays. Brisbane office is by appointment only."}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={100} className="bg-white p-8 md:p-10">
            <ul className="divide-y divide-[var(--abs-line)]">
              {c.hours.map(([day, time]) => (
                <li key={day} className="py-3 flex items-center justify-between text-[15px]">
                  <span className="text-[var(--abs-ink)] font-medium">{day}</span>
                  <span className="text-[var(--abs-mute)]">{time}</span>
                </li>
              ))}
            </ul>
          </ScrollReveal>
        </div>
      </section>

      <LeadCTA />
    </div>
  );
}
